import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, MapPin, Layers, Tag } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { CTAButton } from "@/components/CTAButton";
import { PROJECTS } from "@/data/content";

export default function CaseStudy() {
  const { id } = useParams();
  const navigate = useNavigate();
  const project = PROJECTS.find((p) => p.id === id);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 120]);

  if (!project) {
    return (
      <div className="container-x pt-44 pb-32 text-center">
        <p className="text-taupe mb-6">Project not found.</p>
        <CTAButton to="/portfolio" variant="outline" icon={false}>Back to Portfolio</CTAButton>
      </div>
    );
  }

  const related = PROJECTS.filter((p) => p.id !== project.id && p.category === project.category).slice(0, 3);

  return (
    <div>
      <section className="relative h-[85vh] min-h-[560px] overflow-hidden" data-testid="case-study-hero">
        <motion.img style={{ y }} src={project.image} alt={project.title} className="absolute inset-0 w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/40" />
        <div className="relative h-full container-x flex flex-col justify-end pb-16 md:pb-24 text-white">
          <button onClick={() => navigate(-1)} data-testid="case-study-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/70 hover:text-sand mb-8 self-start">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <Overline light className="mb-6">{project.category}</Overline>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-extrabold leading-[0.9] tracking-tighter">{project.title}</h1>
        </div>
      </section>

      <section className="container-x py-24 md:py-32 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-8">
          <Reveal>
            <Overline className="mb-6">The Project</Overline>
            <p className="font-display text-2xl md:text-3xl font-bold leading-snug">{project.description}</p>
          </Reveal>
        </div>
        <div className="lg:col-span-4">
          <Reveal delay={0.1}>
            <div className="border-t border-charcoal">
              <div className="flex items-center gap-4 py-5 border-b border-black/10">
                <Tag className="h-5 w-5 text-sand" strokeWidth={1.5} />
                <div><div className="eyebrow">Finish</div><div className="text-charcoal">{project.finish}</div></div>
              </div>
              <div className="flex items-center gap-4 py-5 border-b border-black/10">
                <Layers className="h-5 w-5 text-sand" strokeWidth={1.5} />
                <div><div className="eyebrow">Category</div><div className="text-charcoal capitalize">{project.category}</div></div>
              </div>
              <div className="flex items-center gap-4 py-5 border-b border-black/10">
                <MapPin className="h-5 w-5 text-sand" strokeWidth={1.5} />
                <div><div className="eyebrow">Location</div><div className="text-charcoal">{project.location}</div></div>
              </div>
            </div>
            <CTAButton to="/contact" testid="case-study-cta" variant="primary" className="mt-10 w-full justify-center">Start Your Project</CTAButton>
          </Reveal>
        </div>
      </section>

      {/* Large image */}
      <section className="container-x pb-24 md:pb-32">
        <div className="relative aspect-[16/9] overflow-hidden">
          <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-offwhite py-24 md:py-32" data-testid="related-projects">
          <div className="container-x">
            <Overline className="mb-14">More {project.category} Work</Overline>
            <div className="grid md:grid-cols-3 gap-4">
              {related.map((p) => (
                <Link key={p.id} to={`/portfolio/${p.id}`} className="group block relative aspect-[3/4] overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-[800ms] group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-0 p-6 text-white">
                    <div className="text-[0.65rem] uppercase tracking-[0.2em] text-sand">{p.finish}</div>
                    <div className="font-display text-xl font-bold">{p.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
